import createContract from "./createContract";
import erc20Abi from "../erc20.json";
import swal from "sweetalert";
import Web3 from "web3";


// Buffer is expressed in hundred-thousandths (0.00001 = 0.001%)
// For a 0.01% buffer we use 0.0001 as the multiplier: 1 = 0.00001, so 10 = 0.0001 (0.01%)
const BUFFER_BASIS_POINTS = BigInt(10); // 0.01% = 10 / 1_000_000
const BUFFER_DENOMINATOR = BigInt(1_000_000);

function toBaseUnit(amount, decimals) {
  const [wholePartRaw, fractionalPartRaw = ""] = amount.toString().split(".");
  const sanitizedWhole = wholePartRaw.replace(/[^\d]/g, "") || "0";
  const sanitizedFraction = fractionalPartRaw.replace(/[^\d]/g, "");

  const decimalsInt = Number(decimals);
  if (!Number.isInteger(decimalsInt) || decimalsInt < 0) {
    throw new Error("Invalid token decimals");
  }

  let fraction = sanitizedFraction.slice(0, decimalsInt);
  while (fraction.length < decimalsInt) {
    fraction += "0";
  }

  const baseString = `${sanitizedWhole}${fraction}`.replace(/^0+(?=\d)/, "");
  return BigInt(baseString || "0");
}

function formatFromBaseUnits(value, decimals) {
  const decimalsInt = Number(decimals);
  if (!Number.isInteger(decimalsInt) || decimalsInt < 0) {
    throw new Error("Invalid token decimals");
  }

  const multiplier = BigInt(10) ** BigInt(decimalsInt);
  const whole = value / multiplier;
  const fraction = value % multiplier;

  if (fraction === BigInt(0)) {
    return whole.toString();
  }

  const fractionStr = fraction.toString().padStart(decimalsInt, "0").replace(/0+$/, "");
  return `${whole.toString()}.${fractionStr}`;
}

async function approveMint(amounts, currencyId, walletAddress, decimals, network) {
  try {
    // Generate contract for approving mint
    const contract = await createContract(erc20Abi, currencyId);

    const decimalsInt = Number(decimals);
    if (Number.isNaN(decimalsInt)) {
      throw new Error("Invalid token decimals");
    }

    // Convert all amounts to base units
    const totalAmountBase = amounts.reduce((sum, value) => {
      const baseValue = toBaseUnit(value, decimalsInt);
      return sum + baseValue;
    }, BigInt(0));

    // Add buffer in base units based on BUFFER_BASIS_POINTS / BUFFER_DENOMINATOR
    const buffer = (totalAmountBase * BUFFER_BASIS_POINTS) / BUFFER_DENOMINATOR;
    const approvalAmountBase = totalAmountBase + buffer;

    // Get wallet balance in base units
    const balance = await contract.methods.balanceOf(walletAddress).call();
    const balanceBase = BigInt(balance);

    if (balanceBase < approvalAmountBase) {
      const balanceFormatted = formatFromBaseUnits(balanceBase, decimalsInt);
      const approvalFormatted = formatFromBaseUnits(approvalAmountBase, decimalsInt);
      swal(
        "Insufficient Balance",
        `Insufficient balance to approve ${approvalFormatted.toFixed(6)} tokens (including 5% safety buffer). Your balance: ${balanceFormatted.toFixed(6)}`,
        "warning"
      );
      return null;
    }

    console.log("Approval calculation details:", {
      totalAmountBase: totalAmountBase.toString(),
      buffer: buffer.toString(),
      approvalAmountBase: approvalAmountBase.toString(),
      decimals: decimalsInt,
      forwarderAddress: network.FORWARDER_ADDRESS,
      walletAddress,
    });

    const approvalAmountStr = approvalAmountBase.toString();

    const result = await contract.methods
      .approve(network.FORWARDER_ADDRESS, approvalAmountStr)
      .send({ from: walletAddress });
    return result;
  } catch (error) {
    console.error("Error in approveMint:", error);
    
    // Provide user-friendly error messages
    let errorMessage = "An error occurred during token approval";
    
    if (error.message?.includes("User denied")) {
      errorMessage = "Transaction was cancelled by user";
    } else if (error.message?.includes("insufficient funds")) {
      errorMessage = "Insufficient funds for transaction";
    } else if (error.message?.includes("network")) {
      errorMessage = "Network connection error. Please check your connection";
    }
    
    swal(
      "Transaction Error",
      errorMessage,
      "error"
    );
    
    return { error: true, message: errorMessage, originalError: error };
  }
}

export default approveMint;
